import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { usePersonalizationTracker, BridgeApi } from '@skin-seoul/utils';
import { useUserLoggedInKey } from '../../context/userLogginedInKeyContext';
import { WP_JUST_LOGGED_OUT_KEY } from '../../constants/browser/cookie';

/**
 * Shared hook that manages the personalization auth-sync state machine.
 *
 * Encapsulates:
 * - WordPress logout cookie detection and consumption
 * - First-mount initialization (skip resolve in WebView, full resolve otherwise)
 * - Auth state change handling (login → reInitialize, logout → handleExternalLogout)
 *
 * Used by PersonalizationAuthSync (webapp) and ShortcodePersonalizationAuthSync (reactshortcode).
 * For WebView bridge sync, use useNativeBridgeActorSync() alongside this hook.
 */
export function usePersonalizationAuthSync() {
  const client = usePersonalizationTracker();
  const { isLoggedIn } = useUserLoggedInKey();
  const prevIsLoggedInRef = useRef(isLoggedIn);
  const hasInitializedOnceRef = useRef(false);

  const consumeLogoutCookie = useCallback(() => {
    const hasLogoutCookie = Boolean(Cookies.get(WP_JUST_LOGGED_OUT_KEY));
    if (!hasLogoutCookie) {
      return false;
    }

    Cookies.remove(WP_JUST_LOGGED_OUT_KEY);

    prevIsLoggedInRef.current = isLoggedIn;
    client.handleExternalLogout().catch(console.error);

    return true;
  }, [client, isLoggedIn]);

  const initializeIfNeeded = useCallback(() => {
    if (consumeLogoutCookie()) {
      return;
    }

    client.initialize().catch(console.error);
  }, [client, consumeLogoutCookie]);

  const handleAuthStateChange = useCallback(() => {
    if (consumeLogoutCookie()) {
      return;
    }

    if (prevIsLoggedInRef.current === isLoggedIn) {
      return;
    }

    prevIsLoggedInRef.current = isLoggedIn;

    if (isLoggedIn) {
      // Login path: preserve current actorId for merge
      client.reInitialize().catch(console.error);
      return;
    }

    // Logout path: start fresh anonymous actor and clear identity
    client.handleExternalLogout().catch(console.error);
  }, [client, consumeLogoutCookie, isLoggedIn]);

  useEffect(() => {
    const isInWebView = Boolean(BridgeApi.isWebview());

    if (!hasInitializedOnceRef.current) {
      hasInitializedOnceRef.current = true;

      if (isInWebView) {
        // WebView: native app sets cookies before load.
        // Still consume logout cookie and set prevIsLoggedInRef so auth change detection works.
        // Call initialize() so PersonalizationClient reads actorId from cookie into memory.
        // skipResolveWhenSessionValid: true ensures no unnecessary resolve API call.
        consumeLogoutCookie();
        prevIsLoggedInRef.current = isLoggedIn;
        client.initialize().catch(console.error);
        return;
      }

      initializeIfNeeded();
      return;
    }

    handleAuthStateChange();
  }, [initializeIfNeeded, handleAuthStateChange]);
}
